import React, {
  FC, useState, useEffect, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import useGetSalesByTrackingSource from '../../apiHooks/queries/adSalesTrackingSource/getAdSalesByTrackingSource';
import faceBookIcon from '../../assets/icons/facebookSalesIconDashboard.svg';
import GoogleIcon from '../../assets/icons/google-icon-primary.svg';
import bingIcon from '../../assets/icons/bing-icon-primary.svg';
import careDownIcon from '../../assets/icons/caret-down-primary.svg';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import { useGetTrackingSourceBySales } from '../../apiHooks/queries/adManagement/useGetTrackingSources';
import TiktokIcon from '../../assets/icons/tiktok_trackingsource.png';
import LinkedinIcon from '../../assets/icons/linked_ad_icon.png';
import snapchatIcon from '../../assets/icons/snapchat_trackingsource.png';
import emailAtIcon from '../../assets/icons/email-at-icon-primary.svg'
import adtreactionIcon from '../../assets/icons/adtraction.svg';
import affiliateIcon from '../../assets/icons/affiliate.svg';
import contentIcon from '../../assets/icons/content.svg';
import onlineIcon from '../../assets/icons/online.svg';
import vg from '../../assets/icons/VG.svg';
import pinterestIcon from '../../assets/icons/pinterest.svg';
import MailchimpIcon from '../../assets/icons/mailchimp-logo.svg';
import PinMeToIcon from '../../assets/icons/pinmeto-logo.png';



const SalesReportCard: FC = () => {
  const [selectedSource, setSelectedSource] = useState<number>(1);
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { dashboardFilterDateRange } = useDashboardFilterDateStore();
  const { data, isLoading } = useGetSalesByTrackingSource(
    'GET_AD_SALES',
    selectedSource,
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
  );
  const { data: trackingSourcesSales } = useGetTrackingSourceBySales('GET_TRACKING_SOURCES_WITH_SALES');
  const { t } = useTranslation();

  const trackingSourceMap: Record<number, { name: string; icon?: string }> = {
    1: { name: t('googleSales'), icon: GoogleIcon },
    3: { name: t('newsletter'), icon: emailAtIcon },
    4: { name: t('bingSales'), icon: bingIcon },
    5: { name: t('content'), icon : contentIcon },
    6: { name: t('adtraction'), icon: adtreactionIcon },
    7: { name: t('affiliates'), icon: affiliateIcon },
    9: { name: t('onlineContent'), icon: onlineIcon },
    10: { name: t('facebookSales'), icon: faceBookIcon },
    11: { name: t('programmatics') },
    12: { name: t('tiktok'), icon: TiktokIcon },
    13: { name: t('snapchat'), icon: snapchatIcon },
    14: { name: t('vg'), icon: vg },
    15: { name: t('pinterest'), icon: pinterestIcon },
    16: { name: t('linkedin'), icon: LinkedinIcon },
    18: { name: 'Mailchimp', icon: MailchimpIcon },
    19: { name: 'PinMeTo', icon: PinMeToIcon },
  };

  const selectedSourceData = trackingSourceMap[selectedSource] || { name: t('googleSales'), icon: GoogleIcon };
  const hasSales = (trackingSourcesSales || []).some((source) => source.totalSales > 0);

  const handleSelectChange = (value: number) => {
    setSelectedSource(value);
    setShowDropDown(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowDropDown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex h-[150px] w-full flex-col justify-between rounded-[8px] bg-white px-[28px] py-[25px]">
      <div className="flex w-full items-center justify-between">
        <div className="relative w-full">
          <div className="relative flex w-full items-center">
            <div ref={dropdownRef} className="relative inline-block w-full">
              {hasSales ? (
                <>
                  <div
                    onClick={() => setShowDropDown(!showDropDown)}
                    className="border-gray-300 flex w-full items-center justify-start gap-4 rounded-[8px]
                    bg-white py-2 leading-tight text-primary hover:cursor-pointer focus:outline-none focus:ring-primary"
                  >
                    <span>{selectedSourceData.name}</span>
                    <img src={careDownIcon} alt="" className="ml-2" />
                  </div>

                  {showDropDown && (
                    <div className="absolute left-0 mt-2 w-full rounded-[8px] border border-[#E0E0E0] bg-white shadow-lg">
                      {(trackingSourcesSales || [])
                        .filter((source) => source.totalSales > 0)
                        .map((source) => {
                          const sourceMeta = trackingSourceMap[Number(source.trackingSourceId)] || {};
                          return (
                            <div
                              key={source.trackingSourceId}
                              role="button"
                              tabIndex={0}
                              className={`cursor-pointer p-2 text-primary ${
                                selectedSource === Number(source.trackingSourceId) ? 'bg-gray-100' : ''
                              }`}
                              onClick={() => handleSelectChange(Number(source.trackingSourceId))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleSelectChange(Number(source.trackingSourceId));
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                {sourceMeta.icon && (
                                  <img
                                    src={sourceMeta.icon}
                                    alt=""
                                    className={Number(source.trackingSourceId) === 19 ? 'h-5 w-5' : 'h-4 w-4'}
                                  />
                                )}
                                <span>{sourceMeta.name || `Source ${source.trackingSourceId}`}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-500">{t('noSalesFromProviders')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-2 border-b border-b-gray" />

      <div className="flex items-center justify-between">
        <p className="w-full text-[32px] font-medium text-primary">
          {isLoading ? (
            <LoadingSpinner color="primary" size={25} />
          ) : (
            hasSales && (
              <div className="flex w-full items-center justify-between">
                <span>{Math.round(Number(data?.data?.data)).toLocaleString()}</span>
                <img
                  className={selectedSource === 19 ? 'h-8 w-8' : 'h-6 w-6'}
                  src={selectedSourceData.icon}
                  alt=""
                />
              </div>
            )
          )}
        </p>
      </div>
    </div>
  );
};

export default SalesReportCard;
